import { useMemo, useState, type ChangeEvent } from "react";
import { IMPORT_MAX_ROWS, IMPORT_TEMPLATE, itemFor, listingKey, type ImportError, type ImportItem, type ImportPreview, type ImportPreviewRow, type ImportResult } from "../../shared/marketImport";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError, imageUrl } from "../lib/api";
import { useGame } from "../lib/GameProvider";
import { marketApi } from "./api";
import { useArs } from "./components";

const SHEET_ERRORS = ["empty", "no_header", "too_many"];

/** What the seller decided for a row: the product picked (ambiguous rows) and whether to import it. */
interface Choice {
  pick: number | null;
  on: boolean;
}

/** A row with its product chosen: the listing it makes, or why it can't be imported. */
function resolve(row: ImportPreviewRow, pick: number | null): { item: ImportItem | null; error: ImportError | "not_found" | "pick" | null } {
  if (row.status === "invalid") return { item: null, error: row.errors[0] ?? null };
  if (row.status === "not_found") return { item: null, error: "not_found" };
  const c = pick === null ? null : row.candidates[pick];
  if (!c) return { item: null, error: "pick" };
  const made = itemFor(row, c.productId, c.category, c.game);
  return made.ok ? { item: made.item, error: null } : { item: null, error: made.error };
}

/**
 * Load many listings at once: paste rows from a spreadsheet (or upload a CSV), check how each one
 * was read and which catalog product it is, then create them all. Cheap ones go live right away;
 * the rest wait for photos.
 */
export function ListingImport({ pending = false, onClose, onImported }: { /** Shop under review: nothing goes live until approval. */ pending?: boolean; onClose: () => void; onImported: () => void }) {
  const { t, locale } = useI18n();
  const { games } = useGame();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);

  const read = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await marketApi.importPreview(text);
      setPreview(p);
      // Rows already published, or repeated in the sheet, start unchecked.
      const seen = new Set(p.existingKeys);
      setChoices(
        p.rows.map((r) => {
          const pick = r.status === "ok" ? 0 : null;
          const { item } = resolve(r, pick);
          const key = item ? listingKey(item) : null;
          const on = key !== null && !seen.has(key);
          if (key) seen.add(key);
          return { pick, on };
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError && SHEET_ERRORS.includes(err.code) ? t(`imp.sheet.${err.code}` as MessageKey, { max: IMPORT_MAX_ROWS }) : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const loadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) setText(await file.text());
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob(["﻿" + IMPORT_TEMPLATE], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "pokefans-planilla.csv" });
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (result) return <ImportDone result={result} onClose={onClose} />;

  return (
    <section className="new-listing listing-import stack" aria-labelledby="imp-title">
      <h2 id="imp-title">{t("imp.title")}</h2>
      {!preview ? (
        <>
          <p>{t("imp.intro", { max: IMPORT_MAX_ROWS })}</p>
          <ol className="imp-help muted small">
            <li>{t("imp.help1")}</li>
            {games.length > 1 && <li>{t("imp.helpGame", { games: games.map((g) => g.name[locale]).join(", ") })}</li>}
            <li>{t("imp.help2")}</li>
            <li>{t(pending ? "imp.help3Pending" : "imp.help3")}</li>
          </ol>
          <div className="btn-row">
            <button type="button" className="btn btn-outline btn-sm" onClick={downloadTemplate}>
              ⬇ {t("imp.template")}
            </button>
            <label className="btn btn-outline btn-sm">
              📄 {t("imp.file")}
              <input type="file" accept=".csv,.tsv,.txt,text/csv,text/plain" className="sr-only" onChange={loadFile} />
            </label>
          </div>
          <div className="field">
            <label htmlFor="imp-text">{t("imp.paste")}</label>
            <textarea id="imp-text" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("imp.placeholder")} spellCheck={false} />
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="row-actions">
            <button type="button" className="btn btn-primary" disabled={busy || !text.trim()} onClick={read}>
              {busy ? t("imp.reading") : t("imp.read")}
            </button>
            <button type="button" className="btn btn-link" onClick={onClose}>
              {t("cards.cancel")}
            </button>
          </div>
        </>
      ) : (
        <ImportReview
          preview={preview}
          choices={choices}
          setChoices={setChoices}
          onBack={() => setPreview(null)}
          onDone={(r) => {
            setResult(r);
            onImported();
          }}
        />
      )}
    </section>
  );
}

function ImportReview({ preview, choices, setChoices, onBack, onDone }: { preview: ImportPreview; choices: Choice[]; setChoices: (c: Choice[]) => void; onBack: () => void; onDone: (r: ImportResult) => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each row's listing, flagged when already published or when a checked row above makes the same one.
  const rows = useMemo(() => {
    const existing = new Set(preview.existingKeys);
    const above = new Set<string>();
    return preview.rows.map((row, i) => {
      const { item, error: why } = resolve(row, choices[i]?.pick ?? null);
      const key = item ? listingKey(item) : null;
      const duplicate: "published" | "repeated" | null = key === null ? null : existing.has(key) ? "published" : above.has(key) ? "repeated" : null;
      if (key && choices[i]?.on) above.add(key);
      return { row, item, why, duplicate, on: Boolean(item && choices[i]?.on) };
    });
  }, [preview, choices]);
  const chosen = rows.filter((r) => r.on && r.item).map((r) => r.item!);
  const counts = {
    ok: rows.filter((r) => r.item).length,
    pick: rows.filter((r) => r.why === "pick").length,
    bad: rows.filter((r) => r.why && r.why !== "pick").length,
  };
  const update = (i: number, c: Partial<Choice>) => setChoices(choices.map((x, j) => (j === i ? { ...x, ...c } : x)));
  // Picking the product checks the row, unless that listing is already published or checked above.
  const pickFor = (i: number, pick: number) => {
    const { item } = resolve(preview.rows[i], pick);
    const key = item ? listingKey(item) : null;
    const taken = new Set([...preview.existingKeys, ...rows.filter((r, j) => j < i && r.on && r.item).map((r) => listingKey(r.item!))]);
    update(i, { pick, on: key !== null && !taken.has(key) });
  };

  const commit = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await marketApi.importListings(chosen));
    } catch (err) {
      const line = err instanceof ApiError ? ((err.details as { line?: number; lines?: number[] } | undefined)?.line ?? (err.details as { lines?: number[] } | undefined)?.lines?.[0]) : undefined;
      setError(line ? t("imp.rowFailed", { line }) : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="imp-counts" role="status">
        {t("imp.counts", { ok: counts.ok, pick: counts.pick, bad: counts.bad })}
      </p>
      <div className="imp-table-wrap">
        <table className="imp-table">
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">{t("imp.include")}</span>
              </th>
              <th scope="col">{t("imp.line")}</th>
              <th scope="col">{t("imp.product")}</th>
              <th scope="col">{t("imp.details")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <ReviewRow key={r.row.line} {...r} pick={choices[i]?.pick ?? null} onToggle={(on) => update(i, { on })} onPick={(pick) => pickFor(i, pick)} />
            ))}
          </tbody>
        </table>
      </div>
      {error && <p className="form-error">{error}</p>}
      <div className="row-actions">
        <button type="button" className="btn btn-primary" disabled={busy || chosen.length === 0} onClick={commit}>
          {t("imp.commit", { count: chosen.length })}
        </button>
        <button type="button" className="btn btn-link" onClick={onBack}>
          {t("imp.back")}
        </button>
      </div>
    </>
  );
}

function ReviewRow({ row, item, why, duplicate, on, pick, onToggle, onPick }: { row: ImportPreviewRow; item: ImportItem | null; why: string | null; duplicate: "published" | "repeated" | null; on: boolean; pick: number | null; onToggle: (on: boolean) => void; onPick: (i: number) => void }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const chosen = pick === null ? null : row.candidates[pick];
  const typed = [row.card, row.set, row.number && `#${row.number}`].filter(Boolean).join(" · ");
  // The listing as it will be made, or as the sheet says while the product isn't settled.
  const shown = item ?? row.draft;
  return (
    <tr className={on ? "" : "is-off"}>
      <td>
        <input type="checkbox" checked={on} disabled={!item} onChange={(e) => onToggle(e.target.checked)} aria-label={t("imp.includeLine", { line: row.line })} />
      </td>
      <td className="muted">{row.line}</td>
      <td>
        {row.status === "ambiguous" ? (
          <div className="imp-pick">
            <span className="small">
              {t("imp.whichOne")} <span className="muted">({typed})</span>
            </span>
            <select value={pick ?? ""} onChange={(e) => onPick(Number(e.target.value))} aria-label={t("imp.whichOneLine", { line: row.line })}>
              <option value="" disabled>
                {t("imp.choose")}
              </option>
              {row.candidates.map((c, j) => (
                <option key={c.productId} value={j}>
                  {[c.name, c.setName, c.cardNumber && `#${c.cardNumber}`].filter(Boolean).join(" · ")}
                </option>
              ))}
            </select>
          </div>
        ) : chosen ? (
          <div className="imp-product">
            <img src={imageUrl(chosen.imageKey)} alt="" width={30} height={42} loading="lazy" />
            <span>
              <strong>{chosen.name}</strong>
              <span className="muted small">{[chosen.setName, chosen.cardNumber && `#${chosen.cardNumber}`].filter(Boolean).join(" · ")}</span>
            </span>
          </div>
        ) : (
          <span className="muted">{typed || "—"}</span>
        )}
        {why && why !== "pick" && <span className="field-error">{t(`imp.err.${why}` as MessageKey)}</span>}
        {duplicate && <span className="field-hint">{t(duplicate === "published" ? "imp.duplicate" : "imp.repeated")}</span>}
      </td>
      <td>
        <span className="offer-chips">
          {shown.condition && <span className="chip">{shown.condition === "SEALED" ? CONDITION_LABEL.SEALED[locale] : shown.condition}</span>}
          <span className="chip">{LANGUAGE_LABEL[shown.language]?.[locale] ?? shown.language}</span>
          {shown.finish !== "normal" && <span className="chip">{FINISH_LABEL[shown.finish][locale]}</span>}
          {shown.grader && <span className="chip chip-graded">{`${shown.grader} ${shown.grade}`}</span>}
          {row.draft.priceArs !== null && <strong>{ars(row.draft.priceArs)}</strong>}
          {row.draft.quantity > 1 && <span className="muted small">×{row.draft.quantity}</span>}
        </span>
      </td>
    </tr>
  );
}

function ImportDone({ result: r, onClose }: { result: ImportResult; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <section className="new-listing stack" role="status">
      <h2>{t("imp.doneTitle", { count: r.created })}</h2>
      <ul className="imp-done">
        {r.published > 0 && <li>✅ {t("imp.donePublished", { count: r.published })}</li>}
        {r.overLimit > 0 && <li>⏸️ {r.maxLive === null ? t("imp.doneOverPrice", { count: r.overLimit }) : t("imp.doneOverLimit", { count: r.overLimit, max: r.maxLive })}</li>}
        {r.awaitingApproval > 0 && <li>⏳ {t("imp.doneAwaiting", { count: r.awaitingApproval })}</li>}
      </ul>
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t("imp.done")}
        </button>
      </div>
    </section>
  );
}
